import React, { createContext, useContext, useEffect, useState, ReactElement } from 'react';
import { cn } from '../../../utils/classnames';
import { Tag } from '../../tags/Tag';
import { ChevronLeft, ChevronRight } from '@core/icons-react';
import type {
  MessageBranchProps,
  MessageBranchContentProps,
  MessageBranchSelectorProps,
  MessageBranchNavigationProps,
  MessageBranchPageProps,
  MessageBranchContextType,
} from './types';

// ============================================================================
// Context
// ============================================================================

const MessageBranchContext = createContext<MessageBranchContextType | null>(null);

const useMessageBranch = () => {
  const context = useContext(MessageBranchContext);

  if (!context) {
    throw new Error(
      'MessageBranch components must be used within MessageBranch'
    );
  }

  return context;
};

// ============================================================================
// MessageBranch Components
// ============================================================================

/**
 * MessageBranch - container for multiple message branches with navigation
 * Manages state for current branch and provides context to children
 */
export const MessageBranch: React.FC<MessageBranchProps> = ({
  defaultBranch = 0,
  onBranchChange,
  className,
  children,
  ...props
}) => {
  const [currentBranch, setCurrentBranch] = useState(defaultBranch);
  const [branches, setBranches] = useState<ReactElement[]>([]);

  const handleBranchChange = (newBranch: number) => {
    setCurrentBranch(newBranch);
    onBranchChange?.(newBranch);
  };

  const goToPrevious = () => {
    const newBranch =
      currentBranch > 0 ? currentBranch - 1 : branches.length - 1;
    handleBranchChange(newBranch);
  };

  const goToNext = () => {
    const newBranch =
      currentBranch < branches.length - 1 ? currentBranch + 1 : 0;
    handleBranchChange(newBranch);
  };

  const contextValue: MessageBranchContextType = {
    currentBranch,
    totalBranches: branches.length,
    goToPrevious,
    goToNext,
    branches,
    setBranches,
  };

  return (
    <MessageBranchContext.Provider value={contextValue}>
      <div
        className={cn('core-message-branch', className)}
        {...props}
      >
        {children}
      </div>
    </MessageBranchContext.Provider>
  );
};

/**
 * MessageBranchContent - displays the currently active branch
 * Automatically tracks children as branches
 */
export const MessageBranchContent: React.FC<MessageBranchContentProps> = ({
  children,
  className,
  ...props
}) => {
  const { currentBranch, setBranches, branches } = useMessageBranch();
  const childrenArray = Array.isArray(children) ? children : [children];

  // Update branches when children change
  useEffect(() => {
    if (branches.length !== childrenArray.length) {
      setBranches(childrenArray as ReactElement[]);
    }
  }, [childrenArray.length, branches.length, setBranches]);

  return (
    <>
      {childrenArray.map((branch, index) => (
        <div
          className={cn(
            'core-message-branch__content',
            index === currentBranch ? 'core-message-branch__content--active' : 'core-message-branch__content--hidden',
            className
          )}
          key={(branch as ReactElement).key || index}
          {...props}
        >
          {branch}
        </div>
      ))}
    </>
  );
};

/**
 * MessageBranchSelector - wrapper for branch navigation controls
 * Hides automatically when there's only one branch
 */
export const MessageBranchSelector: React.FC<MessageBranchSelectorProps> = ({
  className,
  children,
  ...props
}) => {
  const { totalBranches } = useMessageBranch();

  // Don't render if there's only one branch
  if (totalBranches <= 1) {
    return null;
  }

  return (
    <div
      className={cn('core-message-branch__selector', className)}
      {...props}
    >
      {children}
    </div>
  );
};

/**
 * MessageBranchPrevious - button to navigate to previous branch
 * Wraps around to last branch when at first
 */
export const MessageBranchPrevious: React.FC<MessageBranchNavigationProps> = ({
  children,
  className,
  color: _color,
  ...props
}) => {
  const { goToPrevious, totalBranches } = useMessageBranch();

  return (
    <Tag
      template="action-sm"
      title="Previous branch"
      className={cn('core-message-branch__nav-button', className)}
      disabled={totalBranches <= 1}
      onClick={goToPrevious}
      {...props}
    >
      {children ?? <ChevronLeft size={14} />}
    </Tag>
  );
};

/**
 * MessageBranchNext - button to navigate to next branch
 * Wraps around to first branch when at last
 */
export const MessageBranchNext: React.FC<MessageBranchNavigationProps> = ({
  children,
  className,
  color: _color,
  ...props
}) => {
  const { goToNext, totalBranches } = useMessageBranch();

  return (
    <Tag
      template="action-sm"
      title="Next branch"
      className={cn('core-message-branch__nav-button', className)}
      disabled={totalBranches <= 1}
      onClick={goToNext}
      {...props}
    >
      {children ?? <ChevronRight size={14} />}
    </Tag>
  );
};

/**
 * MessageBranchPage - displays current branch number (e.g., "1 of 3")
 */
export const MessageBranchPage: React.FC<MessageBranchPageProps> = ({
  className,
  ...props
}) => {
  const { currentBranch, totalBranches } = useMessageBranch();

  return (
    <span
      className={cn('core-message-branch__page', className)}
      {...props}
    >
      {currentBranch + 1} of {totalBranches}
    </span>
  );
};

